import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { adminApi } from "@/api/admin";

export const Route = createFileRoute("/admin/users/$uid")({
  component: AdminUserDetailPage,
});

function AdminUserDetailPage() {
  const { uid } = Route.useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-user-projects", uid],
    queryFn: () => adminApi.listUserProjects(uid),
  });

  // Project the user owns surfaces with email-as-title via the projects list
  // already; for this view we only need the projects they belong to.
  const owned = data?.projects.filter((p) => p.role === "owner") ?? [];
  const memberOnly = data?.projects.filter((p) => p.role !== "owner") ?? [];

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/users" className="text-sm text-ink/55 hover:text-ink">
          ← All users
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">
          <span className="font-mono text-base text-ink/55">{uid}</span>
        </h1>
        <p className="mt-1 text-ink/65">
          Every project this user belongs to, split by ownership.
        </p>
      </div>

      {isLoading ? (
        <div className="card p-6 text-ink/55">Loading…</div>
      ) : error ? (
        <div className="card p-6 text-red-600">Couldn't load user's projects.</div>
      ) : (
        <>
          <Section
            title="Projects owned"
            empty="This user doesn't own any project."
            projects={owned}
          />
          <Section
            title="Member of"
            empty="Not a member of any other project."
            projects={memberOnly}
          />
        </>
      )}
    </div>
  );
}

function Section({
  title, empty, projects,
}: {
  title: string;
  empty: string;
  projects: Array<{
    id: string;
    name: string;
    plan_id: string;
    paid_until: string | null;
    suspended: boolean;
    role: "owner" | "developer" | "billing";
    joined_at: string;
  }>;
}) {
  return (
    <section>
      <h2 className="text-sm font-medium uppercase tracking-wider text-ink/55">
        {title} <span className="font-mono text-xs text-ink/45">({projects.length})</span>
      </h2>
      {projects.length === 0 ? (
        <div className="mt-3 card p-4 text-sm text-ink/55">{empty}</div>
      ) : (
        <div className="mt-3 card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Paid until</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-t border-ink/8 hover:bg-cream/50">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to="/admin/projects/$pid"
                      params={{ pid: p.id }}
                      className="text-ink hover:underline"
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{p.plan_id}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                      p.role === "owner" ? "bg-primary-100 text-primary-700" : "bg-ink/5 text-ink/65"
                    }`}>
                      {p.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink/65">{p.paid_until ? new Date(p.paid_until).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    {p.suspended
                      ? <span className="rounded bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-600">suspended</span>
                      : <span className="rounded bg-green-500/15 px-2 py-0.5 text-xs font-medium text-green-600">active</span>}
                  </td>
                  <td className="px-4 py-3 text-ink/55">{new Date(p.joined_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
